import { z } from 'zod';
import { Id, IsoDateTime } from './common.ts';
import { ErrorCode } from './errors.ts';

export const WorkflowRunState = z
  .enum(['runnable', 'running', 'awaiting_signal', 'sleeping', 'succeeded', 'failed', 'cancelled'])
  .meta({ id: 'WorkflowRunState' });
export type WorkflowRunState = z.infer<typeof WorkflowRunState>;

export const WorkflowError = z
  .object({ code: ErrorCode, message: z.string(), details: z.record(z.string(), z.unknown()).optional() })
  .meta({ id: 'WorkflowError' });
export type WorkflowError = z.infer<typeof WorkflowError>;

/** One durable workflow run. `fencingToken` increases every time a worker acquires the lease. */
export const WorkflowRun = z
  .object({
    runId: Id,
    taskId: Id,
    /** Workflow definition name, e.g. `GovernedTaskWorkflow`. */
    definition: z.string().min(1),
    version: z.string().min(1),
    state: WorkflowRunState,
    currentStep: z.string().optional(),
    attempt: z.number().int().nonnegative(),
    fencingToken: z.number().int().nonnegative(),
    lockedBy: z.string().optional(),
    lockedUntil: IsoDateTime.optional(),
    wakeAt: IsoDateTime.optional(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    error: WorkflowError.optional(),
    /** Added by 07: the run in an external engine (Temporal: workflow id, run id, UI link). */
    external: z
      .object({
        system: z.string(),
        workflowId: z.string(),
        runId: z.string(),
        url: z.string().optional(),
      })
      .optional(),
  })
  .meta({ id: 'WorkflowRun' });
export type WorkflowRun = z.infer<typeof WorkflowRun>;

/** A memoized step of a run, keyed by `(runId, name)`. */
export const WorkflowStepRecord = z
  .object({
    runId: Id,
    name: z.string().min(1),
    attempt: z.number().int().nonnegative(),
    status: z.enum(['running', 'succeeded', 'failed']),
    input: z.unknown().optional(),
    output: z.unknown().optional(),
    error: WorkflowError.optional(),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime.optional(),
  })
  .meta({ id: 'WorkflowStepRecord' });
export type WorkflowStepRecord = z.infer<typeof WorkflowStepRecord>;
